import type { JSX } from 'react';
import { formatDegree } from '@/utils/degreeUtils';

interface BirthDetailsPayload {
  date: string;
  time: string;
  place: string;
  fullName: string;
  gender: 'Male' | 'Female';
}

interface PlanetsTableProps {
  birthDetails: BirthDetailsPayload;
  planets: any[];
}

export default function PlanetsTable({ birthDetails, planets }: PlanetsTableProps): JSX.Element {
  if (!birthDetails) {
    return <div className="text-center p-6 text-lg">Birth details missing</div>;
  }

  if (!planets || !planets.length) {
    return <div className="text-center p-6 text-lg">No planetary data found</div>;
  }

  const ascendant = planets.find(
    (p: any) =>
      p.name?.toLowerCase().includes('lagna') ||
      p.name?.toLowerCase().includes('ascendant') ||
      p.name?.toLowerCase() === 'asc'
  );

  const otherPlanets = planets.filter(
    (p: any) =>
      !p.name?.toLowerCase().includes('lagna') &&
      !p.name?.toLowerCase().includes('ascendant') &&
      p.name?.toLowerCase() !== 'asc'
  );

  const sortedPlanets = ascendant ? [ascendant, ...otherPlanets] : planets;

  return (
    <div className="space-y-10 w-full max-w-full overflow-x-hidden">
      <div className="border border-[#2DAAFD] rounded-xl p-6 overflow-x-auto">
        <h3 className="text-3xl font-bold font-spectral mb-6 text-center text-[#2A185C]">
          PLANETARY POSITIONS
        </h3>

        <div className="overflow-x-auto w-full shadow-sm border border-[#06186C]">
          <table className="w-full min-w-[800px] border-collapse border-hidden font-18 whitespace-nowrap">
            <thead className="bg-[#031B5B] text-white uppercase font-spectral font-semibold tracking-wide">
              <tr className="text-center align-middle">
                <th className="border border-[#031B5B] border-r-white/30 px-4 py-4 min-w-[153px] leading-tight">
                  Planet/Asc
                </th>
                <th className="border border-[#031B5B] border-r-white/30 px-4 py-4 min-w-[153px] leading-tight">
                  Sign
                </th>
                <th className="border border-[#031B5B] border-r-white/30 px-4 py-4 leading-tight">
                  Degree
                </th>
                <th className="border border-[#031B5B] border-r-white/30 px-4 py-4 leading-tight">
                  Awastha
                </th>
                <th className="border border-[#031B5B] border-r-white/30 px-4 py-4 leading-tight">
                  Sign Lord
                </th>
                <th className="border border-[#031B5B] border-r-white/30 px-4 py-4 min-w-[120px] leading-tight">
                  Nakshatra
                </th>
                <th className="border border-[#031B5B] border-r-white/30 px-4 py-4 leading-tight">
                  Pada
                </th>
                <th className="border border-[#031B5B] border-r-white/30 px-4 py-4 min-w-[150px] leading-tight">
                  Nakshatra Lord
                </th>
                <th className="border border-[#031B5B] px-4 py-4 leading-tight">House</th>
              </tr>
            </thead>

            <tbody className="text-gray-900 font-normal font-sans">
              {sortedPlanets.map((p: any, i: number) => (
                <tr
                  key={p.id}
                  className={`border-b border-[#031B5B]/30 hover:opacity-95 transition-opacity ${i % 2 === 0 ? 'bg-white' : 'bg-[linear-gradient(to_right,#B7E2FF,#2DAAFD)]'
                    }`}
                >
                  <td className="border-r border-[#031B5B]/30 px-4 py-3 text-left">{p.name}</td>
                  <td className="border-r border-[#031B5B]/30 px-4 py-3 text-left">{p.sign}</td>
                  <td className="border-r border-[#031B5B]/30 px-4 py-3 text-center">
                    {formatDegree(p.sign, p.degree)}
                  </td>
                  <td className="border-r border-[#031B5B]/30 px-4 py-3 text-center">
                    {p.planetAwastha || '--'}
                  </td>
                  <td className="border-r border-[#031B5B]/30 px-4 py-3 text-center">
                    {p.signLord}
                  </td>
                  <td className="border-r border-[#031B5B]/30 px-4 py-3 text-center">
                    {p.nakshatra}
                  </td>
                  <td className="border-r border-[#031B5B]/30 px-4 py-3 text-center">
                    {p.nakshatraPada}
                  </td>
                  <td className="border-r border-[#031B5B]/30 px-4 py-3 text-center">
                    {p.nakshatraLord}
                  </td>
                  <td className="px-4 py-3 text-center">{p.house}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
